// ============================================================================
// Django backend URL surface — aligned to the real Django project in
// wantok-telehealth/backend/ as of the DRF + SimpleJWT + CORS wire-up.
//
// Auth uses SimpleJWT (username + password). JSON response shapes are
// described inline; helpers in `api/*.js` modules tolerate both raw lists
// and paginated DRF `{count, next, previous, results}` envelopes.
// ============================================================================

export const endpoints = {
  // ---- Auth (SimpleJWT) --------------------------------------------------
  // POST { username, password } -> { access, refresh }
  login: '/api/auth/token/',
  // POST { refresh } -> { access }
  refresh: '/api/auth/token/refresh/',
  // GET -> { id, username, email, first_name, last_name, role, profile, is_staff, is_superuser }
  me: '/api/auth/me/',
  // No server-side logout endpoint in backend — client just clears its tokens.
  logout: null,
  // POST { first_name, last_name, sex, password, ... } -> { access, refresh, user }
  // Public patient self-registration; creates User + PatientProfile in one shot.
  registerPatient: '/api/auth/register-patient/',
  passwordResetRequest: '/api/auth/password-reset/request/',
  passwordResetConfirm: '/api/auth/password-reset/confirm/',
  // ---- MFA (email OTP) ---------------------------------------------------
  // Login step 2: POST { mfa_token, code } -> { access, refresh } (or 401).
  mfaVerify: '/api/auth/mfa/verify/',
  // POST -> { enabled, method, recovery_codes:[...] } (shown once)
  mfaEnable: '/api/auth/mfa/enable/',
  mfaDisable: '/api/auth/mfa/disable/',
  mfaRecoveryCodes: '/api/auth/mfa/recovery-codes/',
  mfaStatus: '/api/auth/mfa/status/',
  // TOTP / authenticator app (Phase 2)
  mfaTotpSetup: '/api/auth/mfa/totp/setup/',     // POST -> { secret, otpauth_uri, qr_data_uri }
  mfaTotpConfirm: '/api/auth/mfa/totp/confirm/', // POST { code } -> { enabled, recovery_codes }
  mfaTotpDisable: '/api/auth/mfa/totp/disable/',
  // Login fallback for TOTP users: POST { mfa_token } -> { email_sent }
  mfaSendEmailCode: '/api/auth/mfa/send-email-code/',
  // Platform-admin recovery: POST { user_id, disable? }
  mfaAdminReset: '/api/auth/mfa/admin/reset/',
  // Patient profile + late org self-enrollment (post-signup)
  patientProfileBase: '/api/patients/',
  patientEnrollOrg:   '/api/patients/me/enroll-org/',

  // ---- Patients ----------------------------------------------------------
  // GET ?search=&ordering=&page= -> {count, next, previous, results}
  patientList: '/api/patients/',
  patientDetail: (id) => `/api/patients/${id}/`,
  // Patient Identity Verification (V1) — role-gated action endpoints.
  // Body for verify: { method?: "clinician_verified" | "telehealth_verified" | "document_verified" }
  // Body for flag:   { reason: "photo_outdated" | "photo_mismatch" | "duplicate_suspected" | "name_discrepancy" | "other" }
  patientVerifyIdentity: (id) => `/api/patients/${id}/verify-identity/`,
  patientFlagIdentity:   (id) => `/api/patients/${id}/flag-identity/`,

  // ---- Doctors -----------------------------------------------------------
  doctorList: '/api/doctors/',
  doctorDetail: (id) => `/api/doctors/${id}/`,

  // ---- Operations dashboard (platform admin) ----------------------------
  // GET ?days=&customer_type= -> sections: new_patients + org_activity.
  operationsDashboard: '/api/patients/operations/',

  // ---- Clinical Notes Engine --------------------------------------------
  // Encounters
  encounterList: '/api/clinical-notes/encounters/',
  encounterDetail: (id) => `/api/clinical-notes/encounters/${id}/`,
  encounterCreate: '/api/clinical-notes/encounters/',
  encounterUpdate: (id) => `/api/clinical-notes/encounters/${id}/`,
  encounterSubmit: (id) => `/api/clinical-notes/encounters/${id}/submit/`,
  encounterReopen: (id) => `/api/clinical-notes/encounters/${id}/reopen/`,
  // Clinical notes (1:1 with encounter)
  clinicalNoteList: '/api/clinical-notes/clinical-notes/',
  clinicalNoteDetail: (id) => `/api/clinical-notes/clinical-notes/${id}/`,
  clinicalNoteCreate: '/api/clinical-notes/clinical-notes/',
  // Vital signs (many per encounter)
  vitalSignList: '/api/clinical-notes/vital-signs/',
  vitalSignDetail: (id) => `/api/clinical-notes/vital-signs/${id}/`,
  vitalSignCreate: '/api/clinical-notes/vital-signs/',
  // Diagnoses
  diagnosisList: '/api/clinical-notes/diagnoses/',
  diagnosisDetail: (id) => `/api/clinical-notes/diagnoses/${id}/`,
  diagnosisCreate: '/api/clinical-notes/diagnoses/',
  // Orders
  labOrderList: '/api/clinical-notes/lab-orders/',
  labOrderDetail: (id) => `/api/clinical-notes/lab-orders/${id}/`,
  labOrderCreate: '/api/clinical-notes/lab-orders/',
  imagingOrderList: '/api/clinical-notes/imaging-orders/',
  imagingOrderCreate: '/api/clinical-notes/imaging-orders/',
  prescriptionList: '/api/clinical-notes/prescriptions/',
  prescriptionCreate: '/api/clinical-notes/prescriptions/',
  // POST { pharmacy_id, reason? } → 201 with the new Prescription
  // (clones items, addressed to pharmacy_id, original preserved).
  prescriptionReroute: (id) => `/api/clinical-notes/prescriptions/${id}/reroute/`,
  // Bedside / point-of-care tests — catalogue (RO autocomplete) +
  // per-encounter records. Result `result_unit` and `unit_price_pgk`
  // are auto-snapshotted server-side from the org rate card or the
  // catalogue indicative price; the FE never sends them.
  bedsideTestSearch: '/api/clinical-notes/bedside-tests/',
  bedsideTestRecordList: '/api/clinical-notes/bedside-test-records/',
  bedsideTestRecordCreate: '/api/clinical-notes/bedside-test-records/',
  bedsideTestRecordDetail: (id) => `/api/clinical-notes/bedside-test-records/${id}/`,
  ambulanceList: '/api/clinical-notes/ambulance-requests/',
  ambulanceCreate: '/api/clinical-notes/ambulance-requests/',
  referralList: '/api/clinical-notes/referrals/',
  referralCreate: '/api/clinical-notes/referrals/',
  addendumList: '/api/clinical-notes/addendums/',
  addendumCreate: '/api/clinical-notes/addendums/',
  addendumDetail: (id) => `/api/clinical-notes/addendums/${id}/`,

  // ---- Documents (polymorphic ClinicalDocument) --------------------------
  // GET  /api/documents/?owner_model=&owner_id= -> list
  // POST (multipart) /api/documents/ { owner_model, owner_id, file, kind, description }
  // DELETE /api/documents/{id}/ (doctor/admin only)
  documentList: '/api/documents/',
  documentDetail: (id) => `/api/documents/${id}/`,
  // GET (streams the binary) — authenticated + per-object authorized.
  // The raw /media/clinical-documents/* path is blocked for PHI, so the
  // file MUST be fetched through here with the JWT bearer attached.
  documentDownload: (id) => `/api/documents/${id}/download/`,
  // POST { document_type, encounter_id, payload }
  // 201 -> ClinicalDocument row (with file_url to download/open the PDF)
  // 422 -> {detail: "Encounter has no prescriptions yet — add one before…"}
  documentGenerate: '/api/documents/generate/',
  // POST { to?, cc?, subject?, message? }
  // 200 -> { sent_to, subject, document_id, delivered }
  // 400 -> no recipient (and patient has no email on file)
  documentEmail: (id) => `/api/documents/${id}/email/`,

  // ---- Notifications ------------------------------------------------------
  // GET /api/notifications/?unread=1&kind=foo -> list
  // POST /api/notifications/{id}/mark_read/   -> stamp read_at
  // POST /api/notifications/mark_all_read/    -> { updated: N }
  notificationList: '/api/notifications/',
  notificationMarkRead: (id) => `/api/notifications/${id}/mark_read/`,
  notificationMarkAllRead: '/api/notifications/mark_all_read/',

  // ---- Health -------------------------------------------------------------
  // /api/health/ is liveness (no deps); /api/ready/ checks DB + Redis.
  health: '/api/health/',
  ready: '/api/ready/',

  // ---- Catalogues (live search) -----------------------------------------
  // ICD-10 search: GET ?q= -> { results: [{id, code, title, label, category}] }
  icd10Search: '/api/clinical-notes/icd10/search/',
  // Clinical Dictionary (PR #71) — clinician-facing concept picker.
  // The FE picker calls this in preference to icd10Search. Response:
  //   { results: [{ id, slug, display_name, icd10_id, icd10_code, icd10_title,
  //                 category, requires_modifier, local_only }] }
  // icd10Search remains available for the "search full ICD-10 catalogue"
  // fallback link at the bottom of the picker.
  clinicalConceptsSearch: '/api/clinical-notes/concepts/search/',
  // Provider catalogues are read/write on the DRF endpoints; no dedicated
  // search route, use `?search=` on the list endpoint.
  // Drug search now points at the global Drug formulary (not per-pharmacy
  // catalogue), so doctors see a clean dedup'd 1800-row list regardless
  // of which pharmacy they're sending the prescription to. Pharmacy-
  // specific stock/price/brand is queried separately via /api/pharmacies/stock/.
  drugSearch: '/api/pharmacies/drugs/',
  // Pharmacy stock + catalogue self-service.
  pharmacyStock: '/api/pharmacies/stock/',
  pharmacyStockTemplate: '/api/pharmacies/stock/csv_template/',
  pharmacyStockBulkUpload: '/api/pharmacies/stock/bulk_upload/',
  // Laboratory catalogue self-service.
  labTestSearchV2: '/api/laboratories/tests/',
  labStock: '/api/laboratories/stock/',
  labStockTemplate: '/api/laboratories/stock/csv_template/',
  labStockBulkUpload: '/api/laboratories/stock/bulk_upload/',
  // Radiology catalogue self-service.
  radiologyStock: '/api/radiology/stock/',
  radiologyStockTemplate: '/api/radiology/stock/csv_template/',
  radiologyStockBulkUpload: '/api/radiology/stock/bulk_upload/',
  // Subscribing organizations + their workforce-health dashboard.
  organizations: '/api/organizations/',
  // Minimal active-org list (id + name + type) for the doctor's
  // Register-patient employer dropdown. Doctor/admin only.
  organizationEnrolmentOptions: '/api/organizations/enrolment-options/',
  organizationStaffLinks: '/api/organizations/staff-links/',
  // National rollup of Value & Impact across every active organization.
  // Platform-admin only; powers /superuser/national-dashboard.
  nationalDashboard: '/api/organizations/national/dashboard/',
  // National Health Intelligence — population-health analytics across
  // ALL participating users (org staff, individuals, general public).
  // Platform-admin only; powers /superuser/national.
  nationalIntelligence: '/api/organizations/national/intelligence/',
  // Clinical audit log — platform-admin only. Powers
  // /superuser/audit-log. Append filters as query params.
  auditLogs:        '/api/audit/logs/',
  auditLogsSummary: '/api/audit/logs/summary/',
  auditLogsExport:  '/api/audit/logs/export_csv/',
  // Platform Economics (Phase 6.1) — ICER's own running-cost capture.
  // Platform-admin only. Powers /superuser/platform-costs.
  platformCosts:           '/api/organizations/platform-costs/',
  platformCostsSummary:    '/api/organizations/platform-costs/summary/',
  platformAssumptions:     '/api/organizations/platform-assumptions/',
  // Lab-test search now points at the global LabTest catalogue (not the
  // per-lab catalogue), so doctors see the full ~128-test list regardless
  // of which laboratory they're routing to. Per-lab availability + price
  // is queried separately via /api/laboratories/stock/ + /tests/<id>/stock/.
  labTestSearch: '/api/laboratories/tests/',
  // Imaging-study search now points at the global ImagingStudy catalogue
  // (not the per-centre catalogue), so doctors see the full ~84-study list.
  imagingStudySearch: '/api/radiology/studies/',
  // Organizations: pick the right endpoint per type client-side.
  laboratoryList: '/api/laboratories/',
  pharmacyList: '/api/pharmacies/',
  radiologyList: '/api/radiology/',
  ambulanceServiceList: '/api/ambulance-services/',
  ambulanceItems: '/api/ambulance-services/items/',
  visitServices: '/api/clinical-notes/visit-services/',

  // ---- Health Facility directory (for referrals) ------------------------
  // GET /api/clinical-notes/facilities/?facility_type=&specialty=&search=
  //   -> list of {id, name, facility_type, specialty, town, province, label}
  // GET /api/clinical-notes/facilities/specialties/
  //   -> { results: [distinct specialty strings] }
  facilityList: '/api/clinical-notes/facilities/',
  facilityDetail: (id) => `/api/clinical-notes/facilities/${id}/`,
  facilitySpecialties: '/api/clinical-notes/facilities/specialties/',
}

// ============================================================================
// Real Encounter shape (read):
// {
//   id, patient, patient_name, doctor, doctor_name,
//   consultation_type, status: 'draft'|'submitted'|'closed',
//   encounter_date, chief_complaint, created_at, updated_at,
//   clinical_note: ClinicalNote | null,
//   vital_signs: VitalSign[],
//   diagnoses: Diagnosis[],
//   lab_orders, imaging_orders, prescriptions, ambulance_requests, referrals,
// }
// api/encounters.js adapts this into the legacy flat shape the UI expects:
//   { narrative, structured: { vitals }, icd10_codes: [{code, description}] }
// ============================================================================
